import { useState, useCallback } from 'react';
import api from '../lib/api';
import type { CFCommitteeProfile } from '../types/campaignFinance';
import { CF_REQUEST_TIMEOUT_MS } from '../utils/campaignFinanceUtils';

interface UseCFCommitteeReturn {
  profile: CFCommitteeProfile | null;
  loading: boolean;
  error: string | null;
  load: (coId: string, window?: { start_date?: string; end_date?: string }) => Promise<void>;
  reset: () => void;
}

/**
 * Fetches a committee profile (spine + cycle-scoped summary + name history)
 * from GET /campaign-finance/committees/:co_id. The summary is windowed by the
 * passed cycle dates; pass none for all-time.
 */
export function useCFCommittee(): UseCFCommitteeReturn {
  const [profile, setProfile] = useState<CFCommitteeProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (coId: string, window?: { start_date?: string; end_date?: string }) => {
    if (!coId) return;
    setLoading(true);
    setError(null);
    setProfile(null); // clear the previous committee immediately so it doesn't linger
    try {
      const params = new URLSearchParams();
      if (window?.start_date) params.set('start_date', window.start_date);
      if (window?.end_date) params.set('end_date', window.end_date);
      const qs = params.toString();
      const res = await api.get(`/campaign-finance/committees/${encodeURIComponent(coId)}${qs ? `?${qs}` : ''}`, { timeout: CF_REQUEST_TIMEOUT_MS });
      if (res.data?.success) {
        setProfile(res.data.data as CFCommitteeProfile);
      } else {
        setError(res.data?.error || 'Failed to load committee');
        setProfile(null);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load committee');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setProfile(null);
    setError(null);
    setLoading(false);
  }, []);

  return { profile, loading, error, load, reset };
}
